// BlogWrite.js - Create/Edit blog post
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { blogApi } from '../../Services/Api';
import styles from './BlogWrite.module.css';

const BlogWrite = () => {
  const navigate = useNavigate();
  const { postId } = useParams(); // For editing
  const isEditing = !!postId;
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category_id: ''
  });

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchPost();
    }
  }, [postId]);

  const fetchCategories = async () => {
    try {
      const response = await blogApi.getCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchPost = async () => {
    setLoading(true);
    try {
      // For editing, we need to fetch the post details
      // Note: This requires the post to be accessible (draft/pending/rejected by owner)
      const response = await blogApi.getMyPosts();
      const post = response.data.posts.find(p => p.id === parseInt(postId));
      if (post) {
        setFormData({
          title: post.title,
          content: '', // Need full content - may need separate API call
          category_id: post.category?.id || ''
        });
      }
    } catch (err) {
      setError('Could not load post for editing');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (submitForReview = false) => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }
    if (formData.content.trim().length < 100) {
      setError('Content should be at least 100 characters');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const data = {
        ...formData,
        submit_for_review: submitForReview
      };
      
      if (isEditing) {
        await blogApi.updatePost(postId, data);
      } else {
        await blogApi.createPost(data);
      }
      
      navigate('/blog/my-posts');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const wordCount = formData.content.trim().split(/\s+/).filter(Boolean).length;
  const charCount = formData.content.length;

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>{isEditing ? 'Edit Post' : '✍️ Write a Post'}</h1>
        <p>Share your journey, tips, or inspiration with the community</p>
      </div>

      <div className={styles.editor}>
        {/* Category Selection */}
        <div className={styles.field}>
          <label>Category</label>
          <div className={styles.categoryGrid}>
            {categories.map(cat => (
              <button
                key={cat.id}
                type="button"
                className={`${styles.categoryOption} ${formData.category_id === cat.id ? styles.selected : ''}`}
                onClick={() => handleChange({ target: { name: 'category_id', value: cat.id } })}
              >
                <span className={styles.catIcon}>{cat.icon}</span>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div className={styles.field}>
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Give your post a compelling title..."
            maxLength={200}
          />
          <span className={styles.hint}>{formData.title.length}/200 characters</span>
        </div>

        {/* Content */}
        <div className={styles.field}>
          <label htmlFor="content">Content *</label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            placeholder="Share your story, tips, or thoughts..."
            rows={8}
          />
          <div className={styles.contentStats}>
            <span>{wordCount} words</span>
            <span>{charCount} characters</span>
            {charCount < 100 && <span className={styles.warning}>Min 100 characters required</span>}
          </div>
        </div>

        {/* Error Message */}
        {error && <div className={styles.error}>{error}</div>}

        {/* Guidelines */}
        <div className={styles.guidelines}>
          <h3>📋 Community Guidelines</h3>
          <ul>
            <li>Posts are reviewed before publishing to maintain quality</li>
            <li>Be respectful and supportive of others</li>
            <li>No explicit content, hate speech, or harmful advice</li>
            <li>Share genuine experiences and helpful insights</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.draftButton}
            onClick={() => handleSubmit(false)}
            disabled={saving}
          >
            💾 Save as Draft
          </button>
          <button
            type="button"
            className={styles.submitButton}
            onClick={() => handleSubmit(true)}
            disabled={saving || charCount < 100}
          >
            {saving ? 'Saving...' : '🚀 Submit for Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogWrite;
